import React, { useContext, useEffect, useRef, useMemo, useCallback, useState, FC, useReducer } from "react";
import { observer } from "mobx-react-lite";
import "./columnsStyle.css";
import BoardColumn from "../BoardColumn/BoardColumn";
import { AppContext } from "../../../App";
import { useParams } from "react-router-dom";
import { IFunctionPermissions } from "../../../common/api_interfaces/role/IFunctionPermissions";
import { Context } from "../../../index";
import { BoardContext } from "../../../contexts/BoardContext";
import ScrollContainer from "../../UiLib/ScrollContainer/ScrollContainer";
import { AxiosError } from "axios";
import { hasPermission } from "../../../helpers/hasPermisson";
import { IColumnDisplay } from "../../../common/api_interfaces/column/IColumnDisplay";
import { BoardWithRestrictAccess, IRestrictBoardView } from "../../../common/api_interfaces/board/IRestrictBoardView";
import Loader from "../../Shared/Loader/Loader";
import { hasRestrictedAccessBoard, hasRestrictedAccessColumn } from "../../../helpers/hasRestrictedAccess";

export interface IBoardColumnsProps {
    userAccess: IFunctionPermissions[];
}

const BoardColumns: FC<IBoardColumnsProps> = ({ userAccess }) => {
    const { boardId } = useParams();
    const { store } = useContext(Context);
    const { showToast, handleShowLoader } = useContext(AppContext);
    const boardContext = useContext(BoardContext);
    const { currentZoom, setCurrentZoom, MAX_ZOOM, MIN_ZOOM, boardScrollPosition, setBoardScrollPosition } =
        useContext(AppContext);

    const sliderRef = useRef<HTMLDivElement>(null);
    const scrollLeftRef = useRef(0);
    const [boardsData, setBoardsData] = useState<IRestrictBoardView>();
    const [previewTaskId, setPreviewTaskId] = useState<number>();
    const [previewColumnId, setPreviewColumnId] = useState<number>();

    const saveScrollPosition = useCallback(() => {
        if (sliderRef.current) {
            setBoardScrollPosition([Number(boardId), sliderRef.current.scrollLeft]);
        }
    }, [boardId, setBoardScrollPosition]);

    const checkColumnAccess = useCallback(
        (columnId: number): boolean => {
            const hasPermissonBoard = hasPermission(boardContext.systemAccess, "BoardAction", "view");

            // Проверяем ролевой доступ
            if (hasPermissonBoard) {
                return true;
            }

            // Проверяем доступ на доски
            return !(
                boardContext.accessRestrict &&
                hasRestrictedAccessColumn(boardContext.accessRestrict, columnId, store.user.email)
            );
        },
        [boardContext.accessRestrict, boardContext.systemAccess, store.user.email]
    );

    const checkProxyBoardAccess = useCallback(
        (column: IColumnDisplay): boolean => {
            // Проверяем доступ на доски
            return !(
                boardContext.accessRestrict && column.proxyBoardId !== undefined &&
                hasRestrictedAccessBoard(boardContext.accessRestrict, column.proxyBoardId, store.user.email)
            );
        },
        [boardContext.accessRestrict, store.user.email]
    );

    useEffect(() => {
        const slider = sliderRef.current;
        if (!slider) return;

        slider.addEventListener("scroll", saveScrollPosition);

        if (boardScrollPosition && Number(boardId) === boardScrollPosition[0]) {
            slider.scrollLeft = boardScrollPosition[1];
            scrollLeftRef.current = boardScrollPosition[1];
        }

        return () => {
            slider.removeEventListener("scroll", saveScrollPosition);
        };
    }, [boardId, boardContext.filteredColumns, saveScrollPosition, boardScrollPosition]);

    const handleWheelEvent = useCallback(
        (e: Event) => {
            const wheelEvent = e as WheelEvent;

            if (wheelEvent.ctrlKey) {
                wheelEvent.preventDefault();
                wheelEvent.stopPropagation();

                setCurrentZoom((prevZoom) => {
                    const newZoom = wheelEvent.deltaY < 0 ? prevZoom + 0.05 : prevZoom - 0.05;
                    return Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, newZoom));
                });
            }
        },
        [setCurrentZoom, MIN_ZOOM, MAX_ZOOM]
    );

    useEffect(() => {
        const element = document.querySelector(".zoomable__block");
        if (!element) return;

        const options: AddEventListenerOptions = { passive: false };
        element.addEventListener("wheel", handleWheelEvent, options);

        return () => {
            element.removeEventListener("wheel", handleWheelEvent);
        };
    }, [setCurrentZoom, MIN_ZOOM, MAX_ZOOM]);

    const renderColumns = useCallback(
        (columns: IColumnDisplay[]) =>
            columns.map((column) => (
                <BoardColumn
                    previewColumnId={previewColumnId}
                    previewTaskId={previewTaskId}
                    setPreviewColumnId={setPreviewColumnId}
                    setPreviewTaskId={setPreviewTaskId}
                    key={column.id}
                    column={column}
                    userAccess={userAccess}
                    columnAccess={checkColumnAccess(column.id)}
                    proxyBoardAccess={checkProxyBoardAccess(column)}
                    boardsData={boardsData}
                    currentZoom={currentZoom}
                />
            )),
        [previewColumnId, previewTaskId, userAccess, checkColumnAccess, boardsData, currentZoom]
    );

    const memoizedColumns = useMemo(
        () => renderColumns(boardContext.filteredColumns),
        [boardContext.filteredColumns, renderColumns]
    );

    return (
        <div className="dash__board--form">
            <div className="dash__board--form-column ">
                <ScrollContainer
                    className="dash__board--elem__list slider zoomable__block"
                    draggingClassName="dash__board--elem droppable-column"
                    horizontal={true}
                    vertical={false}
                    hideScrollbars={false}
                    style={{ zoom: "" + currentZoom }}
                >
                    {memoizedColumns}
                </ScrollContainer>
            </div>
        </div>
    );
};

export default BoardColumns;
